import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Urls } from '@share/constants';
import { axiosInstance, getHeaders, getSelectedCurrency, getActivityOccupancies, RootState } from '@share/utils';

import { ActivityAvailabilityResponse, IActivityAvailabilityHold } from '@common-types';
import { isEmpty } from 'lodash';

export interface IActivityAvailabilityState {
    data: ActivityAvailabilityResponse | null;
    hold: IActivityAvailabilityHold | null;
    
    loading: boolean;
    error: string;
    errorMessages: string;
    errorHold: string;
    errorHoldMessages: string;

    selectedModalityCode?: string;
    selectedRateKey?: string;
    selectedSessionCode?: string;
    selectedDate?: string; // chosen operation date (yyyy-MM-dd) within the search window
}

const initialState: IActivityAvailabilityState = {
    data: null,
    hold: null,
    loading: false,
    error: '',
    errorMessages: '',
    errorHold: '',
    errorHoldMessages: '',
};

export const checkAvailability = createAsyncThunk<
    any,
    { ActivityCode: string | undefined },
    { state: RootState }
>(
    'activity-availability/checkAvailability',
    async ({ ActivityCode }, { getState, rejectWithValue }) => {
        const { activityStore, datesStore, roomsStore, loginStore } = getState();
        const { sessionKey } = activityStore;
        const { startDate, endDate } = datesStore;
        const { roomsSelected } = roomsStore;
        const { account } = loginStore;

        if (!startDate || !roomsSelected?.length) {
            return rejectWithValue('Missing required search parameters');
        }

        try {
            const payload = {
                sessionId: sessionKey?.value,
                ActivityCode,
                Residency: 'US',
                Currency: getSelectedCurrency(account) || 'USD',
                From: startDate,
                To: endDate ?? startDate,
                Occupancies: getActivityOccupancies(roomsSelected),
                Language: 'en',
                LifeStyle: loginStore.lifeStyle ?? 'None',
            };

            const response = await axiosInstance.post(Urls.ActivityAvailability, payload, {
                ...getHeaders(),
            });

            // A 200 response can still carry a no-availability error in its body
            // (e.g. Hotelbeds returns { modalities: [], errors: [{ code: 'NO_OFFERS_AVAILABLE', ... }] }).
            // Treat that as a rejection so the booking widget shows the inline message,
            // matching how an HTTP-error response is handled below.
            const responseError = response.data?.errors?.[0];
            if (responseError) {
                const message = responseError.message || 'Failed to check availability';
                const code = responseError.code ?
                                `error.code.activity.${responseError.code}` :
                                message;
                return rejectWithValue({ code, message });
            }

            return response.data;
        } catch (error: any) {
            const message = error?.response?.data?.errors?.[0].message || 'Failed to check availability';
            const code = error?.response?.data?.errors?.[0].code ? 
                            `error.code.activity.${error?.response?.data?.errors?.[0].code}` : 
                            message;
            return rejectWithValue({ code, message });
        }
    }
);

export interface CheckAvailabilityHoldParams {
    activityCode: string;
    rateKey: string;
    startTime?: string;
    serviceDate?: string; // chosen operation date; overrides the search window From for the hold
    onSuccess?: () => void;
    onFailure?: (error: string, errorMessage: string) => void;
}

const GENERIC_ERROR_MESSAGE = 'error-';
const MISSING_ERROR_MESSAGE = 'missing';

export const checkAvailabilityHold = createAsyncThunk<
    any,
    CheckAvailabilityHoldParams,
    { state: RootState }
>(
    'activity-availability/checkAvailabilityWithHold',
    async ({ activityCode, rateKey, startTime, serviceDate, onSuccess, onFailure }, { getState, rejectWithValue }) => {
        const { activityStore, datesStore, roomsStore } = getState();
        const { sessionKey } = activityStore;
        const { startDate, endDate } = datesStore;
        const { roomsSelected } = roomsStore;

        if (!startDate || !roomsSelected?.length) {
            const errorMessage = MISSING_ERROR_MESSAGE;
            onFailure?.(errorMessage, 'There was an error checking availability');
            return rejectWithValue(errorMessage);
        }

        try {
            const payload = {
                activityCode,
                rateKey,
                startTime,
                sessionId: sessionKey?.value,
                from: serviceDate ?? startDate,
                to: serviceDate ?? endDate ?? startDate,
                occupancies: getActivityOccupancies(roomsSelected),
            };

            const response = await axiosInstance.post(Urls.ActivityAvailabilityWithHold, payload, {
                ...getHeaders(),
            });

            if (isEmpty(response.data.errorMessage)) {
                //localStorage.setItem(`activityAvailabilityHold_${rateKey}`, JSON.stringify(response.data));
                onSuccess?.();
            } else {
                onFailure?.(GENERIC_ERROR_MESSAGE, 'There was an error checking availability');
                return rejectWithValue(response.data.errorMessage);
            }
            return response.data;
        } catch (error: any) {
            const message = error?.response?.data?.errors?.[0].message || GENERIC_ERROR_MESSAGE;
            const code = error?.response?.data?.errors?.[0].key ? 
                            error?.response?.data?.errors?.[0].key : 
                            message;

            onFailure?.(code, message);

            return rejectWithValue({ code, message });
        }
    }
);

const activityAvailabilitySlice = createSlice({
    name: 'activity-availability',
    initialState,
    reducers: {
        resetAvailability: (state) => {
            state.data = null;
            state.hold = null;
            state.loading = false;
            state.error = '';
            state.errorMessages = '';
            state.errorHold = '';
            state.errorHoldMessages = '';
            state.selectedModalityCode = undefined;
            state.selectedRateKey = undefined;
            state.selectedSessionCode = undefined;
            state.selectedDate = undefined;
        },
        setSelectedModality: (state, { payload }: PayloadAction<string>) => {
            state.selectedModalityCode = payload;
        },
        setSelectedRate: (state, { payload }: PayloadAction<string>) => {
            state.selectedRateKey = payload;
        },
        setSelectedSession: (state, { payload }: PayloadAction<string>) => {
            state.selectedSessionCode = payload;
        },
        setSelectedDate: (state, { payload }: PayloadAction<string | undefined>) => {
            state.selectedDate = payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(checkAvailability.pending, (state) => {
                state.loading = true;
                state.error = '';
                state.errorMessages = '';
                state.errorHold = '';
                state.errorHoldMessages = '';
                state.data = null;
                state.hold = null;
                state.selectedModalityCode = undefined;
                state.selectedRateKey = undefined;
                state.selectedSessionCode = undefined;
                state.selectedDate = undefined;
            })
            .addCase(checkAvailability.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.data = payload;
                state.hold = null;
            })
            .addCase(checkAvailability.rejected, (state, { payload }) => {
                state.loading = false;
                state.error = (payload as any).code;
                state.errorMessages = (payload as any).message;
            })
            .addCase(checkAvailabilityHold.pending, (state) => {
                state.loading = true;
                state.errorHold = '';
                state.errorHoldMessages = '';
                state.hold = null;
            })
            .addCase(checkAvailabilityHold.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.hold = payload;
            })
            .addCase(checkAvailabilityHold.rejected, (state, { payload }) => {
                state.loading = false;
                state.errorHold = (payload as any).code;
                state.errorHoldMessages = (payload as any).message;
            });
    },
});

export const activityAvailabilityActions = {
    ...activityAvailabilitySlice.actions,
    checkAvailability,
};

export const activityAvailabilityReducer = activityAvailabilitySlice.reducer;
